import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getExamPaper } from "@/apis/exam_papers/getExamPaper";
import { QueryKeys } from "@/lib/query-keys";
import PrintableQuestionsView from "@/components/exam/PrintableQuestionsView";
import { Spinner } from "@/components/spinner";
import { Listbox, ListboxOption, ListboxLabel } from "@/components/listbox";
import { Button } from "@/components/button";
import { getPaperTypeLabel } from "@/constants/exam";
import FilteringQuestionsModal from "./components/FilteringQuestionsModal";
import type { IQuestion } from "@/apis/exam_papers/types";

// Extended question type with module information
interface IQuestionWithModule extends IQuestion {
  moduleIndex: number;
  moduleQuestionIndex: number;
}

const ExamPaperPrintQuestionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedModule, setSelectedModule] = useState<string>("all");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const {
    data: examPaper,
    isLoading,
    error,
  } = useQuery({
    queryKey: [QueryKeys.examPaper, id],
    queryFn: () => getExamPaper(id!),
    enabled: !!id,
  });

  // Collect questions with module information
  const allQuestions: IQuestionWithModule[] = [];

  if (examPaper) {
    if (selectedModule === "all") {
      examPaper.sections.forEach((section, sectionIndex) => {
        if (section.questions) {
          section.questions.forEach((question, questionIndex) => {
            allQuestions.push({
              ...question,
              moduleIndex: sectionIndex + 1,
              moduleQuestionIndex: questionIndex + 1,
            });
          });
        }
      });
    } else {
      const moduleIndex = parseInt(selectedModule) - 1;
      const selectedSection = examPaper.sections[moduleIndex];
      if (selectedSection?.questions) {
        selectedSection.questions.forEach((question, questionIndex) => {
          allQuestions.push({
            ...question,
            moduleIndex: moduleIndex + 1,
            moduleQuestionIndex: questionIndex + 1,
          });
        });
      }
    }
  }

  // Filter questions based on selection
  const questionsToShow = selectedQuestionIds.length > 0
    ? allQuestions.filter((q) => selectedQuestionIds.includes(q.id))
    : allQuestions;

  // Prepare module options for the dropdown
  const moduleOptions = examPaper
    ? [
        { value: "all", label: "All Modules" },
        ...examPaper.sections.map((_, index) => ({
          value: String(index + 1),
          label: `Module ${index + 1}`,
        })),
      ]
    : [];

  // Reset and select all questions when module changes
  useEffect(() => {
    if (allQuestions.length > 0) {
      setSelectedQuestionIds(allQuestions.map((q) => q.id));
    }
  }, [selectedModule, examPaper]);

  // Initialize question selection when data loads
  useEffect(() => {
    if (allQuestions.length > 0 && selectedQuestionIds.length === 0) {
      setSelectedQuestionIds(allQuestions.map((q) => q.id));
    }
  }, [allQuestions.length]);

  const handleQuestionFilterApply = (selectedIds: string[]) => {
    setSelectedQuestionIds(selectedIds);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Error Loading Exam Paper
          </h2>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : "An error occurred"}
          </p>
        </div>
      </div>
    );
  }

  if (!examPaper) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            Exam Paper Not Found
          </h2>
          <p className="text-gray-500">
            The requested exam paper could not be found.
          </p>
        </div>
      </div>
    );
  }

  if (allQuestions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            No Questions Available
          </h2>
          <p className="text-gray-500">
            This exam paper does not contain any questions to print.
          </p>
        </div>
      </div>
    );
  }

  if (questionsToShow.length === 0) {
    return (
      <div>
        {/* Module Filter - Hidden when printing */}
        <div className="print:hidden mb-6 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Select Module:
            </label>
            <Listbox
              value={selectedModule}
              onChange={setSelectedModule}
              className="flex-1 max-w-xs"
            >
              {moduleOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>

            <Button
              onClick={() => setIsFilterModalOpen(true)}
              className="whitespace-nowrap"
            >
              Select Questions ({selectedQuestionIds.length}/
              {allQuestions.length})
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              No Questions Selected
            </h2>
            <p className="text-gray-500">
              Please select questions to print using the "Select Questions"
              button.
            </p>
          </div>
        </div>

        {/* Filter Modal */}
        <FilteringQuestionsModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleQuestionFilterApply}
          questions={allQuestions}
          selectedQuestionIds={selectedQuestionIds}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Module Filter - Hidden when printing */}
      <div className="print:hidden mb-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-4">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Select Module:
          </label>
          <Listbox
            value={selectedModule}
            onChange={setSelectedModule}
            className="flex-1 max-w-xs"
          >
            {moduleOptions.map((option) => (
              <ListboxOption key={option.value} value={option.value}>
                <ListboxLabel>{option.label}</ListboxLabel>
              </ListboxOption>
            ))}
          </Listbox>

          <Button
            onClick={() => setIsFilterModalOpen(true)}
            className="whitespace-nowrap"
          >
            Select Questions ({selectedQuestionIds.length}/{allQuestions.length}
            )
          </Button>
        </div>
      </div>

      {/* Printable Questions */}
      <PrintableQuestionsView
        questions={questionsToShow}
        title={`${examPaper.title} - ${getPaperTypeLabel(examPaper.subject)}${
          selectedModule !== "all" ? ` (Module ${selectedModule})` : ""
        }`}
        examType={examPaper.exam_type}
        subject={examPaper.subject}
      />

      {/* Filter Modal */}
      <FilteringQuestionsModal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleQuestionFilterApply}
        questions={allQuestions}
        selectedQuestionIds={selectedQuestionIds}
      />
    </div>
  );
};

export default ExamPaperPrintQuestionsPage;
